import { z } from "zod";

export const createGroupSchema = z.object({
  groupTitle: z.string().nonempty("Title is required!"),
  groupDescription: z.string().nonempty("Description is required!"),
  monthlyContribution: z.string().refine(
    (val) => {
      const parsed = parseInt(val, 10);
      return !Number.isNaN(parsed) && parsed > 0;
    },
    {
      message: "Must be greater than 0",
    }
  ),
  dueDate: z.date(),
  // Payout cadence — how long each member holds a slot before the pot rotates.
  // No zod defaults: the create form always supplies these via defaultValues,
  // and the backend independently defaults a missing cadence to monthly. Using
  // .default() here would make the resolver's input type diverge from
  // CreateGroupData (optional vs required) and break useForm's typing.
  intervalUnit: z.enum(["day", "week", "month"]),
  intervalCount: z.number().int().min(1),
  minMembers: z
    .number()
    .min(2, "A circle needs at least 2 people")
    .max(100, "Maximum members cannot exceed 100"),
  maxMembers: z
    .number()
    .min(2, "A circle needs at least 2 people")
    .max(100, "Maximum members cannot exceed 100"),
  isPrivate: z.boolean(),
  members: z
    .array(
      z.object({
        id: z.string(),
        firstName: z.string(),
        lastName: z.string(),
        email: z.string().email("Invalid email format"),
        displayImg: z.string().nullable(),
        username: z.string().optional(),
      })
    )
    .min(1, "At least one participant is required"),
  currency: z
    .enum(["USD", "EUR", "GBP", "JPY", "CAD"])
    .default("USD")
    .optional(),
});

type CreateGroupData = z.infer<typeof createGroupSchema>;

export const signInSchema = z.object({
  email: z.string().min(1, "Email is required"),
  password: z.string().min(1, "Password is required"),
});

type SignInData = z.infer<typeof signInSchema>;

export const step1Schema = z.object({
  firstName: z.string().min(1, "Required"),
  middleName: z.string().optional(), // or .nullable() if you prefer
  lastName: z.string().min(1, "Required"),
  email: z.string().email("Invalid email"),
  password: z.string().min(8, "Min 8 characters"),
  // Foundational (not KYC): drives the user's currency. Collected at signup so every
  // user has one from the start; identity verification / Stripe stays deferred.
  country: z.enum(["US", "CA"]),
});


export const registerSchema = step1Schema;

export const STEP_FIELDS = {
  step1: ["firstName", "middleName", "lastName", "email", "password"] as const,
};

// Onboarding schema (collected after signup)
// Address object matching Stripe's format
export const onboardingAddressSchema = z.object({
  line1: z.string().min(1, "Street address is required"),
  line2: z.string().optional(),
  city: z.string().min(1, "City is required"),
  state: z.string().min(1, "State/Province is required"),
  postal_code: z.string().min(1, "Postal code is required"),
});

// Step 1: Address and Phone combined
export const onboardingContactSchema = z.object({
  address: onboardingAddressSchema,
  country: z.string().min(1, "Country is required"),
  phone: z
    .string()
    .min(1, "Phone number is required")
    .refine(
      (val) => val.replace(/\D/g, "").length === 10,
      "Please enter a valid 10-digit phone number"
    ),
});

// Step 2: DOB only
export const onboardingDobSchema = z.object({
  dateOfBirth: z
    .string()
    .min(1, "Date of birth is required")
    .refine((date) => {
      const birthDate = new Date(date);
      const today = new Date();
      const age = today.getFullYear() - birthDate.getFullYear();
      return age >= 18 ;
    }, "You must be at least 18 years old"),
});

export const fullOnboardingSchema = onboardingContactSchema.merge(onboardingDobSchema);

export type OnboardingAddress = z.infer<typeof onboardingAddressSchema>;
export type OnboardingContactForm = z.infer<typeof onboardingContactSchema>;
export type OnboardingDobForm = z.infer<typeof onboardingDobSchema>;
export type FullOnboardingForm = z.infer<typeof fullOnboardingSchema>;

type RegisterForm = z.infer<typeof registerSchema>;

export type { CreateGroupData, SignInData,RegisterForm };
